<template>
  <!-- Modal AQI Info -->
  <div v-if="visible" class="fixed inset-0 z-70 overflow-y-auto bg-gray-900/60 backdrop-blur-sm" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <!-- Overlay -->
    <div class="fixed inset-0 transition-opacity" aria-hidden="true" @click="cerrarModal"></div>

    <!-- Contenedor del Modal -->
    <div class="flex min-h-full items-center justify-center p-4 text-center">

      <!-- Panel del Modal -->
      <div class="relative bg-white rounded-lg shadow-xl w-full max-w-lg sm:max-w-xl md:max-w-3xl lg:max-w-5xl max-h-[95vh] flex flex-col overflow-hidden text-left transform transition-all">

        <!-- Header -->
        <div class="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-4 sm:p-5 flex-shrink-0">
          <div class="flex items-center justify-between">
            <h2 class="text-lg sm:text-xl font-bold" id="modal-title">
              CALIDAD DE AIRE INTERIOR DE LA TIENDA
            </h2>
            <button @click="cerrarModal" class="w-8 h-8 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center transition-colors" aria-label="Cerrar modal">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Cuerpo del Modal (con scroll) -->
        <div class="p-4 sm:p-6 overflow-y-auto">

          <h3 class="text-xl font-semibold text-gray-900 mb-2">¿Cómo funciona el AQI?</h3>

          <p class="text-sm text-gray-600 leading-relaxed mb-6">
            Piense en el AQI como un criterio que va de 0 a 500. Cuanto mayor sea el valor del AQI, mayor será el nivel de contaminación del aire y mayor será el problema de salud. Por ejemplo, un valor de AQI de 50 o menos representa una buena calidad del aire, mientras que un valor de AQI superior a 300 representa una calidad de aire peligrosa. Para cada contaminante, un valor AQI de 100 generalmente corresponde a una concentración de aire ambiental que iguala el nivel de la norma nacional de calidad del aire ambiental a corto plazo para la protección de la salud pública. Los valores de AQI iguales o inferiores a 100 generalmente se consideran satisfactorios. Cuando los valores de AQI están por encima de 100, la calidad del aire no es saludable: al principio para ciertos grupos sensibles de personas, luego para todos a medida que los valores de AQI aumentan.
          </p>

          <!-- Tabla de AQI -->
          <div class="overflow-x-auto border border-gray-200 rounded-lg">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50 hidden sm:table-header-group">
                <tr>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Color diario de AQI
                  </th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Niveles de preocupación
                  </th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valores del índice
                  </th>
                  <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descripción de la calidad del aire
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <!-- Fila Verde -->
                <tr class="block sm:table-row">
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-white bg-green-500 block sm:table-cell">
                    <span class="sm:hidden font-bold">Color: </span>Verde
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 block sm:table-cell">
                    <span class="sm:hidden font-bold">Nivel: </span>Bueno
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-700 block sm:table-cell">
                    <span class="sm:hidden font-bold">Índice: </span>0 hasta 50
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600 leading-relaxed block sm:table-cell sm:whitespace-normal">
                    <span class="sm:hidden font-bold">Descripción: </span>La calidad del aire es satisfactoria y la contaminación del aire presenta poco o ningún riesgo.
                  </td>
                </tr>

                <!-- Fila Amarillo -->
                <tr class="block sm:table-row">
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 bg-yellow-400 block sm:table-cell">
                    <span class="sm:hidden font-bold">Color: </span>Amarillo
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 block sm:table-cell">
                    <span class="sm:hidden font-bold">Nivel: </span>Moderado
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-700 block sm:table-cell">
                    <span class="sm:hidden font-bold">Índice: </span>51 hasta 100
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600 leading-relaxed block sm:table-cell sm:whitespace-normal">
                    <span class="sm:hidden font-bold">Descripción: </span>La calidad del aire es aceptable. Sin embargo, puede haber un riesgo para algunas personas, especialmente aquellas que son inusualmente sensibles a la contaminación del aire.
                  </td>
                </tr>

                <!-- Fila Naranja -->
                <tr class="block sm:table-row">
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-white bg-orange-500 block sm:table-cell">
                    <span class="sm:hidden font-bold">Color: </span>Naranja
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 block sm:table-cell">
                    <span class="sm:hidden font-bold">Nivel: </span>No saludable para grupos sensibles
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-700 block sm:table-cell">
                    <span class="sm:hidden font-bold">Índice: </span>101 hasta 150
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600 leading-relaxed block sm:table-cell sm:whitespace-normal">
                    <span class="sm:hidden font-bold">Descripción: </span>Los miembros de grupos sensibles pueden experimentar efectos sobre la salud. Es menos probable que el público en general se vea afectado.
                  </td>
                </tr>

                <!-- Fila Rojo -->
                <tr class="block sm:table-row">
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-white bg-red-500 block sm:table-cell">
                    <span class="sm:hidden font-bold">Color: </span>Rojo
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 block sm:table-cell">
                    <span class="sm:hidden font-bold">Nivel: </span>Insalubre
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-700 block sm:table-cell">
                    <span class="sm:hidden font-bold">Índice: </span>151 hasta 200
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600 leading-relaxed block sm:table-cell sm:whitespace-normal">
                    <span class="sm:hidden font-bold">Descripción: </span>Algunos miembros del público en general pueden experimentar efectos sobre la salud; los miembros de grupos sensibles pueden experimentar efectos de salud más graves.
                  </td>
                </tr>

                <!-- Fila Púrpura -->
                <tr class="block sm:table-row">
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-white bg-purple-600 block sm:table-cell">
                    <span class="sm:hidden font-bold">Color: </span>Púrpura
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 block sm:table-cell">
                    <span class="sm:hidden font-bold">Nivel: </span>Muy Insalubre
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-700 block sm:table-cell">
                    <span class="sm:hidden font-bold">Índice: </span>201 hasta 300
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600 leading-relaxed block sm:table-cell sm:whitespace-normal">
                    <span class="sm:hidden font-bold">Descripción: </span>Alerta de salud: el riesgo de efectos en la salud aumenta para todos.
                  </td>
                </tr>

                <!-- Fila Granate -->
                <tr class="block sm:table-row">
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-white bg-red-900 block sm:table-cell">
                    <span class="sm:hidden font-bold">Color: </span>Granate
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 block sm:table-cell">
                    <span class="sm:hidden font-bold">Nivel: </span>Peligroso
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-700 block sm:table-cell">
                    <span class="sm:hidden font-bold">Índice: </span>301 y superior
                  </td>
                  <td class="px-4 py-3 text-sm text-gray-600 leading-relaxed block sm:table-cell sm:whitespace-normal">
                    <span class="sm:hidden font-bold">Descripción: </span>Advertencia de salud de condiciones de emergencia: todos tienen más probabilidades de verse afectados.
                  </td>
                </tr>

              </tbody>
            </table>
          </div>

        </div>

        <!-- Footer -->
        <div class="bg-gray-50 px-4 py-3 sm:px-6 border-t border-gray-200 flex-shrink-0">
          <div class="flex justify-end">
            <button @click="cerrarModal" type="button" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
              Cerrar
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ModalAqiInfo',

  props: {
    /**
     * Controla la visibilidad del modal
     */
    visible: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close'],
  methods: {
    /**
     * Emite el evento para cerrar el modal
     */
    cerrarModal() {
      this.$emit('close');
    },

    /**
     * Maneja la tecla 'Escape' para cerrar el modal
     */
    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.visible) {
        this.cerrarModal();
      }
    }
  },

  mounted() {
    // Añadir listener para la tecla Escape
    document.addEventListener('keydown', this.handleEscapeKey);
  },
  watch: {
    visible(val) {
      document.body.classList.toggle('modal-open', val)
    },
  },
  beforeUnmount() {
    document.body.classList.remove('modal-open');
    // Limpiar listener al destruir el componente
    document.removeEventListener('keydown', this.handleEscapeKey);
  }
}
</script>

<style scoped></style>